/**
 * Maps known English RBAC/API error bodies to i18n keys so Arabic (and other) locales display translated toasts.
 * Unknown strings are returned unchanged (e.g. new backend messages or dynamic content).
 */

const EXACT_MESSAGE_TO_I18N_KEY = {
  "Role is assigned to users; remove assignments first": "rbac.apiMsgs.roleAssignedRemoveFirst",
  "Duplicate Role is not allowed": "rbac.duplicateRoleNotAllowed",
  "Something went wrong. Please try again later.": "rbac.genericServerError",
  "Role not found": "rbac.apiMsgs.roleNotFound",
  "Not allowed to access this role": "rbac.apiMsgs.notAllowedToAccessRole",
  "The Admin role cannot be modified.": "rbac.apiMsgs.adminRoleCannotBeModified",
  "Default roles cannot be modified.": "rbac.apiMsgs.defaultRoleCannotBeModified",
  "Role name is required": "rbac.roleNameRequired",
  "Role name must be at least 3 characters": "rbac.roleNameMinLength",
  "Role name must be at most 25 characters": "rbac.roleNameMaxLength",
  "Role name is reserved: Admin, Company, and Restaurant Admin are not allowed":
    "rbac.apiMsgs.roleNameReserved",
  "restaurantId is required when user manages multiple restaurants":
    "rbac.apiMsgs.restaurantIdRequiredMultiStore",
  "forCompanyUserId must be a company account user id": "rbac.apiMsgs.forCompanyUserIdInvalid",
  "Company user has no stores": "rbac.apiMsgs.companyUserHasNoStores",
  "sourceRoleId is required": "rbac.apiMsgs.sourceRoleIdRequired",
  "Source role not found": "rbac.apiMsgs.sourceRoleNotFound",
  "Source role belongs to another restaurant": "rbac.apiMsgs.sourceRoleOtherRestaurant",
  "At least one permission is required": "rbac.permissionRequiredForCreate",
  "permissions must be an array of strings (use [] or omit for no permissions yet)":
    "rbac.apiMsgs.permissionsMustBeArray",
  "Invalid permissions": "rbac.apiMsgs.invalidPermissions",
  "Please choose a store-level role for this team member": "rbac.apiMsgs.chooseStoreLevelRole",
  "This type of role cannot be created with your account": "rbac.apiMsgs.roleTypeNotAllowedForAccount",
  "Please select which store this new role belongs to": "rbac.apiMsgs.selectStoreForNewRole",
  "restaurantId is required (add restaurantId or ensure at least one store on the account)":
    "rbac.apiMsgs.restaurantIdRequiredWithHint",
  "userId, roleId, and scopeType are required": "rbac.apiMsgs.userRoleScopeRequired",
  "User not found": "rbac.apiMsgs.userNotFound",
  "Role assignments apply to staff users only": "rbac.apiMsgs.roleAssignmentsStaffOnly",
  "User cannot be assigned in this restaurant": "rbac.apiMsgs.userCannotAssignInRestaurant",
  "This user is already assigned to this role": "rbac.apiMsgs.userAlreadyAssignedRole",
  "Invalid scopeIds": "rbac.apiMsgs.invalidScopeIds",
  "Invalid roleId": "rbac.apiMsgs.invalidRoleId",
  "Assignment not found": "rbac.apiMsgs.assignmentNotFound",
  "Server error": "rbac.apiMsgs.serverError",
  "Invalid restaurantId format": "rbac.apiMsgs.invalidRestaurantIdFormat",
  "No restaurant found for this user": "rbac.apiMsgs.noRestaurantForUser",
  "Not allowed for this restaurant": "rbac.apiMsgs.notAllowedRestaurant",
  "No restaurants configured for employing company account": "rbac.apiMsgs.noRestaurantsCompanyAccount",
  "Unsupported user type": "rbac.apiMsgs.unsupportedUserType",
  "Restaurant (store) not found": "rbac.apiMsgs.restaurantStoreNotFound",
  "scopeIds must be empty for restaurant scope": "rbac.apiMsgs.scopeIdsEmptyForRestaurant",
  "scopeIds required for store scope": "rbac.apiMsgs.scopeIdsRequiredStore",
  "store scope may only include stores you manage": "rbac.apiMsgs.storeScopeOnlyManaged",
  "store scope must list store ids owned by the same restaurant account":
    "rbac.apiMsgs.storeScopeSameAccount",
  "scopeIds required for branch scope": "rbac.apiMsgs.scopeIdsRequiredBranch",
  "Invalid scopeType": "rbac.apiMsgs.invalidScopeType",
  "Invalid branch format.": "rbac.apiMsgs.invalidBranchFormat",
  "Access denied. You are not authorized to view any stores.":
    "rbac.apiMsgs.accessDeniedNoStores",
  "Access denied. No branch scope assigned.": "rbac.apiMsgs.accessDeniedNoBranchScope",
  "Branch not in your accessible scope.": "rbac.apiMsgs.branchNotInScope",
  "Role not found for this restaurant": "rbac.apiMsgs.roleNotFoundForRestaurant",
  "Role is not defined on your account": "rbac.apiMsgs.roleNotOnAccount",
  "Cannot assign account-level roles with your account type":
    "rbac.apiMsgs.cannotAssignAccountLevelRole",
  "Not allowed to assign this role": "rbac.apiMsgs.notAllowedToAssignThisRole",
  "A seller account (restaurantId) is required to assign this role":
    "rbac.apiMsgs.restaurantIdRequiredToAssignRole",
  "Role has no name; cannot resolve store-scoped role":
    "rbac.apiMsgs.roleNameMissingForStoreResolve",
  "This role is account-level. Create a role with the same name for this seller account, or pick a store-level role.":
    "rbac.apiMsgs.accountLevelRoleNeedStoreCopy",
  "Staff user not found": "rbac.apiMsgs.staffUserNotFound",
  "Unauthorized": "rbac.apiMsgs.unauthorized",
  "Forbidden": "rbac.apiMsgs.forbidden",
  "Forbidden: insufficient permissions": "rbac.apiMsgs.forbiddenInsufficientPermissions",
  "You do not have permission to perform this action. This permission is not available on your account—ask your administrator for access.":
    "rbac.apiMsgs.permissionNotAvailableGeneric",
  "You do not have permission to manage branch mappings. Ask your administrator to enable Stores & branches — Manage branch mappings on your role.":
    "rbac.apiMsgs.permissionDeniedManageBranchMappings",
  "You do not have permission to manage auto accept orders. Ask your administrator to enable Stores & branches — Manage auto accept orders on your role.":
    "rbac.apiMsgs.permissionDeniedManageAutoAccessOrders",
  "You do not have permission to manage add-on settings. Ask your administrator to enable Stores & branches — Manage add-on settings on your role.":
    "rbac.apiMsgs.permissionDeniedManageAddonSettings",
  "You do not have permission to enable or disable inventory. Ask your administrator to enable Inventory — Enable/disable inventory on your role.":
    "rbac.apiMsgs.permissionDeniedEnableDisableInventory",
  "You do not have permission to enable or disable auto accept inventory. Ask your administrator to enable Inventory — Enable/disable Auto Accept inventory on your role.":
    "rbac.apiMsgs.permissionDeniedEnableDisableInventory",
  "You do not have permission to view TrackHub settings. Ask your administrator to enable TrackHub settings — View on your role.":
    "rbac.apiMsgs.permissionDeniedTrackHubSettingsView",
  "You do not have permission to update TrackHub settings. Ask your administrator to enable TrackHub settings — Update on your role.":
    "rbac.apiMsgs.permissionDeniedTrackHubSettingsUpdate",
  "You do not have permission to view shipments. Ask your administrator to enable Manual shipments — View on your role.":
    "rbac.apiMsgs.permissionDeniedShipmentsView",
  "You do not have permission to create manual shipments. Ask your administrator to enable Manual shipments — Create on your role.":
    "rbac.apiMsgs.permissionDeniedShipmentsCreate",
  "You do not have permission to cancel shipments. Ask your administrator to enable Manual shipments — Cancel on your role.":
    "rbac.apiMsgs.permissionDeniedShipmentsCancel",
  "You do not have permission to export shipments. Ask your administrator to enable Manual shipments — Export on your role.":
    "rbac.apiMsgs.permissionDeniedShipmentsExport",
  "You do not have permission to view shipment reports. Ask your administrator to enable Shipment reports — View on your role.":
    "rbac.apiMsgs.permissionDeniedShipmentReportsView",
  "You do not have permission to export shipment reports. Ask your administrator to enable Shipment reports — Export on your role.":
    "rbac.apiMsgs.permissionDeniedShipmentReportsExport",
  "Staff user has no restaurant assignment; anchor the user or set employing company profile":
    "rbac.apiMsgs.staffNoRestaurantAssignment",
  "This username is already in use. Please pick another.": "addUserWizard.usernameTaken",
  "This email is already registered to another account": "addUserWizard.emailTaken",
  "This phone number is already registered to another account": "addUserWizard.phoneTaken",
  "Username must be between 3 and 64 characters": "addUserWizard.usernameLengthApi",
  "Username may only contain letters, numbers, dots, underscores, and hyphens (no spaces)":
    "addUserWizard.invalidUsernameChars",
};

const BRANCH_NOT_FOUND_RE =
  /^Branch (.+?) not found (under your stores|under this restaurant account)$/;

/**
 * @param {unknown} message - `err.response?.data?.message`
 * @param {import('i18next').TFunction} t
 * @returns {string}
 */
export function translateRbacApiMessage(message, t) {
  if (message == null || message === "") return "";
  const s = String(message).trim();
  const exactKey = EXACT_MESSAGE_TO_I18N_KEY[s];
  if (exactKey) return t(exactKey);

  const branchMatch = BRANCH_NOT_FOUND_RE.exec(s);
  if (branchMatch) {
    const id = branchMatch[1];
    const scopeKey =
      branchMatch[2] === "under your stores"
        ? "rbac.apiMsgs.scopeUnderYourStores"
        : "rbac.apiMsgs.scopeUnderThisRestaurantAccount";
    return t("rbac.apiMsgs.branchNotFoundPattern", {
      id,
      detail: t(scopeKey),
    });
  }

  return s;
}
